/**
 * Module dependencies.
 */

import type { AppProps } from 'next/app';
import { CacheProvider, Global } from '@emotion/react';
import { MenuProvider } from 'components/providers/menu-provider/menu-provider';
import { ThemeProvider } from '@mui/material/styles';
import { Toaster } from 'react-hot-toast';
import { appWithTranslation } from 'next-i18next';
import { emotionCacheInstance } from 'utils/emotion-cache';
import { globalStyle } from 'styles/global-style';
import { theme } from 'styles/theme/theme';
import { useEffect } from 'react';
import { useRouter } from 'next/router';
import Head from 'next/head';

/**
 * `Props` type.
 */

type Props = AppProps & {
  emotionCache: any;
};

/**
 * `App` component.
 */

function App({
  Component,
  emotionCache = emotionCacheInstance,
  pageProps,
}: Props) {
  const router = useRouter();

  useEffect(() => {
    if (!router?.events) {
      return;
    }

    const handleRouteChange = () => {
      requestAnimationFrame(() => {
        window.scrollTo(0, 0);
        document.body.scrollTo({ behavior: 'smooth', top: 0 });
      });
    };

    router.events.on('routeChangeComplete', handleRouteChange);

    return () => {
      router.events.off('routeChangeComplete', handleRouteChange);
    };
  }, [router.events]);

  return (
    <CacheProvider value={emotionCache}>
      <ThemeProvider theme={theme}>
        <MenuProvider>
          <Global styles={globalStyle} />

          <Head>
            <meta
              content={
                'width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=no'
              }
              name={'viewport'}
            />

            <link href={'/favicon.ico'} rel={'icon'} />
          </Head>

          <Component {...pageProps} />
        </MenuProvider>

        <Toaster />
      </ThemeProvider>
    </CacheProvider>
  );
}

/**
 * Export `App` component.
 */

export default appWithTranslation(App);
